import { Dialog } from "@constellation-academy/epic-ui";
import { useFormik } from "formik";
import { Button } from "primereact/button";
import { useState } from "react";
import * as Yup from "yup";
import { useTranslation } from "@i18n/hooks";
import { ValidatedField } from "../../../components/ValidatedField";
import { SelectProductField } from "../select-product-field";
import {
	type Product,
	type SelectProductFieldDialogProps,
	type SelectProductFieldDialogState,
} from "../select-product-field-dialog/select-product-field-dialog.interface";

type GenericSelectProductFieldDialogProps = {
	productType: {
		emptyText: string;
		buttonLabel: string;
		dialogHeader: string;
		fieldLabel: string;
	};
} & SelectProductFieldDialogProps;

export const GenericSelectProductFieldDialog = ({
	updateField,
	fieldValue: selectedProduct,
	disabled,
	onChange,
	productType,
}: GenericSelectProductFieldDialogProps) => {
	const { t } = useTranslation("common");
	const [isModalOpen, setModalOpen] = useState<boolean>(false);

	const formik = useFormik<SelectProductFieldDialogState>({
		initialValues: {
			product: selectedProduct,
		},
		validationSchema: Yup.object().shape({}),
		onSubmit: (values) => {
			updateField({
				id: values.product?.id,
				title: values.product?.title,
			});
			setModalOpen(false);
			onChange?.();
		},
	});

	const handleDialogOnHide = () => {
		setModalOpen(false);
	};

	const handleSelectProductOnClick = () => {
		setModalOpen(true);
	};

	const handleUnselectProductOnClick = () => {
		updateField(undefined);
	};

	const name = selectedProduct?.id ? selectedProduct?.title : productType.emptyText;

	return (
		<>
			<div className="flex">
				<div className="flex-grow-1 flex bg-gray-100 border-gray-300 border-1 border-round  px-3 py-2">
					{name}
				</div>
				<Button
					className="w-auto p-button-secondary p-2 ml-2"
					type={"button"}
					disabled={!selectedProduct?.id || disabled}
					tooltip={t("product.deleteTooltip")}
					icon="pi pi-times"
					onClick={handleUnselectProductOnClick}
				/>
				<Button
					className="w-auto ml-2"
					type={"button"}
					disabled={disabled}
					onClick={handleSelectProductOnClick}
					label={productType.buttonLabel}
				/>
			</div>
			{/* epic-ui's Dialog (not primereact's) so this stacks above an epic-ui dialog it is
			    nested in - epic-ui bundles its own primereact copy with a separate z-index stack.
			    It takes no className, hence the sized wrapper instead of the former w-10. */}
			<Dialog
				title={productType.dialogHeader}
				onHide={handleDialogOnHide}
				visible={isModalOpen}
			>
				<form onSubmit={formik.handleSubmit} style={{ width: "80vw" }}>
					<ValidatedField<SelectProductFieldDialogState, Product>
						className="mb-4"
						name={"product"}
						label={productType.fieldLabel}
						formikConfig={{
							...formik,
							errors: { ...formik.errors, product: undefined },
						}}
						component={SelectProductField}
					/>
					<Button
						label={t("product.select")}
						icon="pi pi-check"
						type="submit"
						autoFocus
					/>
				</form>
			</Dialog>
		</>
	);
};
